import { Component, Element, h, Host, Listen, Prop, State } from '@stencil/core';
import { BACKGROUND_COLOR, BackgroundColor } from './models/global-header.types';

@Component({
  tag: 'wink-global-header',
  styleUrl: 'styles/global-header.default.scss',
  shadow: true,
})
export class GlobalHeader {
  @Element() host!: HTMLWinkGlobalHeaderElement;

  @Prop({ reflect: true }) backgroundColor: BackgroundColor = BACKGROUND_COLOR.default;

  @State() hasCustomerOrientation? = false;
  @State() hasIntentBar? = false;
  @State() hasCustomerZone? = false;
  @State() hasLanguage? = false;
  @State() hasBrand? = false;
  @State() hasMainNavigation? = false;
  @State() hasSearch? = false;
  @State() hasCallToAction? = false;
  @State() hasTopNavigation = false;
  @State() hasMobileCustomerOrientation? = false;
  @State() hasMobileIntentBar? = false;
  @State() hasMobileCustomerZone? = false;
  @State() hasMobileLanguage? = false;
  @State() hasMobileBrand? = false;
  @State() hasMobileMainNavigation? = false;
  @State() hasMobileSearch? = false;
  @State() hasMobileCallToAction? = false;
  @State() IsMobileMinimal = false;
  @State() hasFlyout = false;
  @State() hasMobileFlyout = false;
  @State() hasScrolled = false;
  @State() hasBreadcrumbs = false;
  @State() hasItemIntentBar = false;
  @State() hasMobileItemIntentBar = false;

  @State() isSubNavOpen = false;
  @State() isMobileOpen = false;
  @State() hasKeyboardFocus = false;

  private wasRecentKeyboardActivity = false;
  private lastKeyPressed = '';

  @Listen('focusin', { target: 'document' })
  handleFocusIn(event: FocusEvent) {
    const target = event.target as HTMLElement;
    const isInHeader = target.closest('wink-global-header') === this.host;
    if (isInHeader) {
      const isKeyboardFocus =
        this.wasRecentKeyboardActivity ||
        target.matches(':focus-visible') ||
        ['Tab', 'ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Enter', ' '].includes(this.lastKeyPressed);
      if (isKeyboardFocus) {
        this.hasKeyboardFocus = true;
      }
    }
  }

  @Listen('focusout', { target: 'document' })
  handleFocusOut(event: FocusEvent) {
    const relatedTarget = event.relatedTarget as HTMLElement;
    const isStillInHeader = relatedTarget?.closest('wink-global-header') === this.host;
    if (!isStillInHeader) {
      this.hasKeyboardFocus = false;
    }
  }

  @Listen('keydown', { target: 'document' })
  handleKeyDown(event: KeyboardEvent) {
    this.lastKeyPressed = event.key;
    this.wasRecentKeyboardActivity = true;
    setTimeout(() => {
      this.wasRecentKeyboardActivity = false;
    }, 100);
  }

  @Listen('mainNavigationItemInteracted', { target: 'document' })
  mainNavigationItemInteracted(event: CustomEvent<{ index: number; name: string }>) {
    if (this.host.querySelector(`[slot="mobile-flyout"][flyout-id="${event.detail.index}"]`)) {
      this.isSubNavOpen = true;
      return;
    }
    this.isSubNavOpen = false;
  }

  @Listen('flyoutBackLinkClicked', { target: 'document' })
  handleFlyoutBackLinkClicked() {
    this.isSubNavOpen = false;
  }

  @Listen('scroll', { target: 'window' })
  handleScroll() {
    const hasScrolledTemp = this.hasScrolled;
    this.hasScrolled = window.scrollY > 10;

    if (hasScrolledTemp !== this.hasScrolled) {
      this.setHeightVariable();
    }
  }

  componentWillLoad() {
    this.hasCustomerOrientation = !!this.host.querySelector('[slot="customer-orientation"]');
    this.hasIntentBar = !!this.host.querySelector('[slot="intent-bar"]');
    this.hasCustomerZone = !!this.host.querySelector('[slot="customer-zone"]');
    this.hasLanguage = !!this.host.querySelector('[slot="language"]');
    this.hasItemIntentBar = !!this.host.querySelector('[slot="utility-bar"]');

    this.hasBrand = !!this.host.querySelector('[slot="brand"]');
    this.hasMainNavigation = !!this.host.querySelector('[slot="main-navigation"]');
    this.hasSearch = !!this.host.querySelector('[slot="search"]');
    this.hasCallToAction = !!this.host.querySelector('[slot="call-to-action"]');

    this.hasMobileCustomerOrientation = !!this.host.querySelector('[slot="mobile-customer-orientation"]');
    this.hasMobileIntentBar = !!this.host.querySelector('[slot="mobile-intent-bar"]');
    this.hasMobileCustomerZone = !!this.host.querySelector('[slot="mobile-customer-zone"]');
    this.hasMobileLanguage = !!this.host.querySelector('[slot="mobile-language"]');
    this.hasMobileItemIntentBar = !!this.host.querySelector('[slot="mobile-utility-bar"]');

    this.hasMobileBrand = !!this.host.querySelector('[slot="mobile-brand"]');
    this.hasMobileMainNavigation = !!this.host.querySelector('[slot="mobile-main-navigation"]');
    this.hasMobileSearch = !!this.host.querySelector('[slot="mobile-search"]');
    this.hasMobileCallToAction = !!this.host.querySelector('[slot="mobile-call-to-action"]');

    this.hasFlyout = !!this.host.querySelector('[slot="flyout"]');
    this.hasMobileFlyout = !!this.host.querySelector('[slot="mobile-flyout"]');

    this.hasBreadcrumbs = !!this.host.querySelector('[slot="breadcrumbs"]');

    this.hasTopNavigation = this.hasCustomerOrientation || this.hasIntentBar || this.hasCustomerZone;

    this.IsMobileMinimal =
      (this.hasMobileLanguage || this.hasMobileItemIntentBar) &&
      !this.hasMobileCustomerOrientation &&
      !this.hasMobileIntentBar &&
      !this.hasMobileCustomerZone &&
      !this.hasMobileMainNavigation &&
      !this.hasMobileSearch &&
      !this.hasMobileCallToAction;
  }

  componentDidLoad() {
    this.setHeightVariable();
  }

  private setHeightVariable() {
    requestAnimationFrame(() => {
      const clientHeight = this.host.clientHeight > 30 ? this.host.clientHeight : 64;
      document.body.style.setProperty('--wink-global-header-height', `${clientHeight}px`);
    });
  }

  private toggleMobileMenu(event: Event) {
    this.isMobileOpen = !this.isMobileOpen;
    const target = event.target as HTMLInputElement;
    target.checked ? document.body.classList.add('wink-no-scroll') : document.body.classList.remove('wink-no-scroll');
  }

  private mobileMenuKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleMobileMenu(event);
    }
  }

  private showBottomRow() {
    return this.hasBrand ?? this.hasMainNavigation ?? this.hasSearch ?? this.hasCallToAction;
  }

  private getTopNavigation() {
    if (this.hasTopNavigation) {
      return (
        <div
          class={{
            'top-row': true,
            'hidden-when-scrolled': this.hasScrolled && !this.hasKeyboardFocus,
          }}>
          {this.hasCustomerOrientation && <slot name="customer-orientation" />}
          {this.hasIntentBar && <slot name="intent-bar" />}
          {this.hasCustomerZone && <slot name="customer-zone" />}
          {this.hasLanguage && <slot name="language" />}
          {this.hasItemIntentBar && <slot name="utility-bar" />}
        </div>
      );
    }
  }

  private getBottomRow() {
    if (this.showBottomRow()) {
      return (
        <div class="bottom-row">
          {this.hasBrand && <slot name="brand" />}
          {this.hasMainNavigation && <slot name="main-navigation" />}
          {this.hasSearch && <slot name="search" />}
          {this.hasCallToAction && <slot name="call-to-action" />}
          {!this.hasTopNavigation && this.hasLanguage && <slot name="language" />}
          {this.hasItemIntentBar && <slot name="utility-bar" />}
        </div>
      );
    }
  }

  private getMobileBrand() {
    if (this.hasMobileBrand) {
      return (
        <div class={`mobile-row ${this.isMobileOpen ? 'open' : ''}`}>
          {this.hasMobileBrand && <slot name="mobile-brand" />}
          <div class="mobile-row-right">
            {this.hasMobileCustomerZone && <slot name="mobile-customer-zone" />}
            {this.hasMobileSearch && <slot name="mobile-search" />}
            <div class="mobile-hamburger">
              {!this.IsMobileMinimal && (
                <button
                  class="mobile-hamburger-button"
                  aria-label="Menu"
                  aria-expanded={`${this.isMobileOpen}`}
                  onClick={(event) => this.toggleMobileMenu(event)}
                  onKeyDown={(event) => this.mobileMenuKeydown(event)}>
                  {this.isMobileOpen ? (
                    <wink-icon appearance="secondary" icon="cross" size="md" class="mobile-hamburger-button close" />
                  ) : (
                    <wink-icon appearance="transparent" icon="hamburger-menu" size="md" class="mobile-hamburger-button open" />
                  )}
                </button>
              )}
              {this.getMobileFlyout()}
            </div>
          </div>
        </div>
      );
    }
  }

  private getFlyout() {
    if (this.hasFlyout && this.showBottomRow()) {
      return (
        <div class="flyout-container">
          <slot name="flyout" />
          <div class="line" />
        </div>
      );
    }
  }

  private getMobileFlyout() {
    return (
      <div class="mobile-fly-out">
        <div class="mobile-fly-out-top">
          <div class="mobile-fly-out-top-main">
            {this.hasMobileMainNavigation && <slot name="mobile-main-navigation" />}
            {this.hasMobileCallToAction && <slot name="mobile-call-to-action" />}
            {this.hasMobileIntentBar && <slot name="mobile-intent-bar" />}
          </div>
          <div class={`mobile-fly-out-top-sub-nav ${this.isSubNavOpen ? 'open' : ''}`}>
            {this.hasMobileFlyout && <slot name="mobile-flyout" />}
          </div>
        </div>
        <div class="mobile-fly-out-bottom">
          {this.hasMobileCustomerOrientation && <slot name="mobile-customer-orientation" />}
          {this.hasMobileLanguage && <slot name="mobile-language" />}
          {this.hasMobileItemIntentBar && <slot name="mobile-utility-bar" />}
        </div>
      </div>
    );
  }

  render() {
    return (
      <Host class={{ compact: !this.hasTopNavigation, minimal: this.IsMobileMinimal }}>
        <div class="wrapper-external">
          <div class="wrapper">
            {this.getTopNavigation()}
            {this.getBottomRow()}
            {this.getFlyout()}
            {this.getMobileBrand()}
          </div>
        </div>
        {this.hasBreadcrumbs && (
          <div class={`breadcrumbs ${this.isSubNavOpen ? 'breadcrumbs-hidden' : ''}`}>
            <div class="breadcrumbs breadcrumbs--inner">
              <slot name="breadcrumbs" />
            </div>
          </div>
        )}
      </Host>
    );
  }
}
